import { some, extend, find } from 'lodash';
import PaymentsScreen from '../../components/PaymentsScreen/PaymentsScreen';
import PrivacyScreen from '../../components/PrivacyScreen/PrivacyScreen';
import UserDetailsForm from '../../containers/UserDetails/UserDetailsForm';
import InsuranceLists from '../../components/InsuranceLists/InsuranceLists';
import {
  ACCIDENT_COMPENSATION_PATH,
  CASCO_COMPENSATION_PATH,
  MEDICAL_COMPENSATION_PATH,
  CONTRACT_SUBSCRIPTION_PATH,
  PROPERTY_COMPENSATION_PATH,
  RECURRING_PATH,
  TRAVEL_CALCULATOR_PATH,
  TRAVEL_COMPENSATION_PATH,
  TRAVEL_CONTRACT_SIGN_PATH,
  ONCOCARE_CONTRACT_SIGN_PATH,
  ONCOCARE_CALCULATOR_PATH,
} from '../../constants/routes';
import Vehicles from '../../components/Vehicles/Vehicles';
import ManufacturersTable from '../../components/Vehicles/ManufacturersTable/ManufacturersTable';
import MyCardsPage from '../../components/MyCards/MyCardsPage/MyCardsPage';
import FailedPayments from '../../components/FailedPayments';
import PaymentNotifications from '../../components/PaymentNotifications';
import PaymentAttempts from '../../components/PaymentAttempts';
import { clearApplicantSearch } from '../../redux/modules/searchApplicant';

export const nonAuthRouteTree = {
  children: [
    {
      title: 'landingPage.calculatorMenu.header',
      children: [
        {
          title: 'landingPage.calculatorMenu.travel',
          path: TRAVEL_CALCULATOR_PATH,
        },
        {
          title: 'landingPage.calculatorMenu.oncoCare',
          path: ONCOCARE_CALCULATOR_PATH,
        },
      ],
    },
    {
      title: 'landingPage.contractSigningMenu.header',
      children: [
        {
          title: 'landingPage.contractSigningMenu.travel',
          path: `${TRAVEL_CONTRACT_SIGN_PATH}/signer`,
        },
        {
          title: 'landingPage.contractSigningMenu.oncoCare',
          path: `${ONCOCARE_CONTRACT_SIGN_PATH}/signer`,
        },
      ],
    },
    {
      title: 'landingPage.contractMenu.header',
      children: [
        {
          title: 'landingPage.contractMenu.contractValidation',
          path: '/contracts/validation',
        },
        {
          title: 'landingPage.contractMenu.makePayment',
          path: `${RECURRING_PATH}/schedule`,
        },
        {
          title: 'landingPage.contractMenu.attachCard',
          path: `${CONTRACT_SUBSCRIPTION_PATH}/policy`,
        },
      ],
    },
    {
      title: 'landingPage.compensationMenu.header',
      children: [
        {
          title: 'landingPage.compensationMenu.applicationStatus',
          path: '/claim-status',
        },
        {
          title: 'landingPage.compensationMenu.applications',
          path: '/claim-applications',
          children: [
            {
              title: 'landingPage.compensationMenu.travel',
              path: `${TRAVEL_COMPENSATION_PATH}/searchApplicant`,
              actionName: clearApplicantSearch,
            },
            {
              title: 'landingPage.compensationMenu.accident',
              path: `${ACCIDENT_COMPENSATION_PATH}/searchApplicant`,
              actionName: clearApplicantSearch,
            },
            {
              title: 'landingPage.compensationMenu.cascoClaim',
              path: `${CASCO_COMPENSATION_PATH}/searchApplicant`,
              actionName: clearApplicantSearch,
            },
            {
              title: 'landingPage.compensationMenu.medical',
              path: `${MEDICAL_COMPENSATION_PATH}/searchApplicant`,
              actionName: clearApplicantSearch,
            },
            {
              title: 'landingPage.compensationMenu.property',
              path: `${PROPERTY_COMPENSATION_PATH}/searchApplicant`,
            },
          ],
        },
      ],
    },
  ],
};
export const authRouteTree = {
  children: [
    {
      title: 'landingPage.reportMenu.header',
      children: [
        {
          title: 'landingPage.reportMenu.contracts',
          path: '/report/contracts',
          component: InsuranceLists,
        },
        {
          title: 'landingPage.reportMenu.payment',
          path: '/report/payment',
          component: PaymentsScreen,
        },
      ],
    },
    {
      title: 'landingPage.configMenu.header',
      children: [
        {
          title: 'landingPage.configMenu.personalData',
          path: '/generalInfo/personalData',
          component: UserDetailsForm,
        },
        {
          title: 'landingPage.configMenu.changePassword',
          path: '/generalInfo/privacy',
          component: PrivacyScreen,
        },
        {
          title: 'landingPage.configMenu.vehicles',
          path: '/generalInfo/vehicles',
          component: Vehicles,
          isAdminRoute: true,
        },
        {
          title: 'landingPage.configMenu.manufacturers',
          path: '/generalInfo/manufacturers',
          component: ManufacturersTable,
          isAdminRoute: true,
        },
        {
          title: 'landingPage.configMenu.myCards',
          path: '/generalInfo/my-cards',
          component: MyCardsPage,
        },
        {
          title: 'landingPage.configMenu.paymentAttempts',
          path: '/generalInfo/payment-attempts',
          component: PaymentAttempts,
        },
        {
          title: 'landingPage.configMenu.failedPayments',
          path: '/generalInfo/failed-payments',
          component: FailedPayments,
          isAdminRoute: true,
        },
        {
          title: 'landingPage.configMenu.paymentNotifications',
          path: '/generalInfo/payment-notifications',
          component: PaymentNotifications,
          isAdminRoute: true,
        },
      ],
    },
  ],
};

export const languageTree = {
  title: 'language',
  children: [
    {
      title: 'armenian',
    },
    {
      title: 'english',
    },
  ],
};

export function findParentNode(parent, node) {
  if (!parent.children) {
    return null;
  }

  if (some(parent.children, (child) => child.title === node.title)) {
    return parent;
  }

  for (let i = 0; i < parent.children.length; i += 1) {
    const res = findParentNode(parent.children[i], node);
    if (res) {
      return res;
    }
  }

  return null;
}

export function getFlattenRoutes(root) {
  const stack = [];
  const array = [];
  const hashMap = {};
  stack.push(root);

  while (stack.length !== 0) {
    const node = stack.pop();
    if (!node.children) {
      if (!hashMap[node.path]) {
        hashMap[node.path] = true;
        array.push(node);
      }
    } else {
      if (node.path && !hashMap[node.path]) {
        hashMap[node.path] = true;
        const nodeCopy = extend({}, node);
        delete nodeCopy.children;
        array.push(nodeCopy);
      }
      for (let i = node.children.length - 1; i >= 0; i -= 1) {
        stack.push(node.children[i]);
      }
    }
  }
  return array;
}

export function findSelectedMenu(routeTree, pathname) {
  return find(routeTree, (item) => {
    if (item.children) {
      return some(item.children, (l) => pathname.indexOf(l.path) === 0);
    }
    return pathname.indexOf(item.path) === 0;
  });
}

export const redirectTo = (history, path, e) => {
  e.preventDefault();
  e.stopPropagation();
  history.push(path);
};
